import React, { useEffect, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { brand } from '../theme';
import { getAnnotationCategory } from '../utils/annotationCategories';
import { pluralize } from '../utils/format';
import { formatTimecode } from '../utils/timecode';
import ProTimeline from './Timeline/ProTimeline';
import type { TimelineLane } from './Timeline/timelineTypes';
import { useMediaPlayer } from './Timeline/useMediaPlayer';
import TransportBar from './AnnotationStudio/TransportBar';
import VideoStage, { useFullscreen } from './AnnotationStudio/VideoStage';
import { annotationsActiveAt, isRangeAnnotation } from './AnnotationStudio/annotationModel';
import { studioDark } from './AnnotationStudio/studioTokens';

interface Annotation {
  id: string;
  time: number;
  text: string;
  category?: string;
  color?: string;
  endTime?: number;
}

interface VideoPreviewWithTimelineProps {
  videoUrl: string;
  videoName: string;
  annotations: Annotation[];
  /** Height of the picture in px. */
  height?: number;
  showControls?: boolean;
  onTimeUpdate?: (currentTime: number) => void;
  onAnnotationClick?: (annotation: Annotation) => void;
}

/**
 * Read-only review player used on the creator's project pages: the shared
 * transport, the creator's notes on the picture as they come up, and a
 * timeline of the notes (click to jump).
 */
const VideoPreviewWithTimeline: React.FC<VideoPreviewWithTimelineProps> = ({
  videoUrl,
  videoName,
  annotations,
  height = 400,
  showControls = true,
  onTimeUpdate,
  onAnnotationClick,
}) => {
  const player = useMediaPlayer();
  const fullscreen = useFullscreen<HTMLDivElement>();
  const { currentTime } = player;

  useEffect(() => {
    onTimeUpdate?.(currentTime);
  }, [currentTime, onTimeUpdate]);

  // Older records can lack ids; the timeline and lists need stable ones.
  const safeAnnotations = useMemo(
    () => (Array.isArray(annotations) ? annotations : [])
      .filter((a) => a && Number.isFinite(Number(a.time)))
      .map((a, index) => (a.id ? a : { ...a, id: `note-${index}-${a.time}` })),
    [annotations]
  );

  const active = useMemo(() => annotationsActiveAt(safeAnnotations, currentTime, 2), [safeAnnotations, currentTime]);
  const stageNotes = active.map((annotation) => {
    const category = getAnnotationCategory(annotation.category, annotation.color);
    return {
      id: annotation.id,
      label: `${category.label} · ${formatTimecode(annotation.time)}`,
      text: annotation.text,
      color: category.color,
    };
  });

  const lanes = useMemo<TimelineLane[]>(() => [{
    id: 'notes',
    label: 'Notes',
    icon: <ChatBubbleOutlineIcon />,
    kind: 'markers',
    emptyText: 'No notes on this video',
    items: safeAnnotations.map((annotation) => {
      const category = getAnnotationCategory(annotation.category, annotation.color);
      return {
        id: annotation.id,
        start: annotation.time,
        end: isRangeAnnotation(annotation) ? annotation.endTime : null,
        label: annotation.text,
        color: category.color,
        ariaLabel: `${category.label}: ${annotation.text}`,
      };
    }),
  }], [safeAnnotations]);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 3, borderColor: brand.border }}>
      <Box ref={fullscreen.ref} sx={{ bgcolor: studioDark.stage, display: 'flex', flexDirection: 'column', ...(fullscreen.isFullscreen ? { height: '100vh' } : {}) }}>
        <VideoStage
          videoUrl={videoUrl}
          player={player}
          notes={stageNotes}
          fullscreen={fullscreen.isFullscreen}
          maxHeight={`${height}px`}
        />
        {showControls && (
          <TransportBar
            player={player}
            fps={30}
            markers={safeAnnotations.map((annotation) => ({
              time: annotation.time,
              endTime: annotation.endTime,
              color: getAnnotationCategory(annotation.category, annotation.color).color,
            }))}
            isFullscreen={fullscreen.isFullscreen}
            onToggleFullscreen={fullscreen.toggle}
          />
        )}
      </Box>

      {showControls && (
        <Box sx={{ p: 1.5 }}>
          <ProTimeline
            duration={player.duration}
            currentTime={currentTime}
            playing={player.playing}
            lanes={lanes}
            onSeek={player.seek}
            onItemSelect={(item) => {
              const annotation = safeAnnotations.find((a) => a.id === item.id);
              if (annotation) onAnnotationClick?.(annotation);
            }}
            title={videoName}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 0.5, fontVariantNumeric: 'tabular-nums' }}>
            {pluralize(safeAnnotations.length, 'note')}
            {player.duration > 0 ? ` · ${formatTimecode(player.duration)} long` : ''}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default VideoPreviewWithTimeline;
